<template>
  <div class="versions-tab-content">
    <VersionsTable
      :config="config"
      :filtered-versions="versionManager.filteredVersions.value"
      :loading="versionManager.loading.value"
      :error="versionManager.error.value"
      :available-authors="versionManager.availableAuthors.value"
      @preview="handlePreview"
      @edit-comment="handleEditComment"
      @copy="handleCopy"
      @clear-filters="versionManager.clearFilters"
      @author-filter="versionManager.setAuthorFilter"
    />

    <!-- Comment Dialog (Phase 5) -->
    <VersionCommentDialog
      v-model="commentDialogOpen"
      :version="selectedVersion"
      :asset-type="config.assetType"
      :asset-id="config.assetId"
      @saved="handleCommentSaved"
    />

    <AtlasSnackbar
      v-model="snackbar.show"
      :severity="snackbar.severity"
      :text="snackbar.message"
      :timeout="3000"
    />

    <!-- Save Preview as Current Confirmation Dialog (T064) -->
    <AtlasDialog
      v-model="saveConfirmDialogOpen"
      eyebrow="VERSIONS"
      :title="t('common.saveAsCurrentVersion').value"
      max-width="500"
      persistent
      @close="saveConfirmDialogOpen = false"
    >
      {{ t('common.savePreviewWarning') }}
      <template #actions>
        <AtlasButton
          variant="ghost"
          @click="saveConfirmDialogOpen = false"
        >
          {{ t('common.cancel') }}
        </AtlasButton>
        <AtlasButton
          :loading="savingPreview"
          @click="handleSavePreviewAsCurrent"
        >
          {{ t('common.save') }}
        </AtlasButton>
      </template>
    </AtlasDialog>
  </div>
</template>

<script setup lang="ts">
import { AtlasButton, AtlasDialog, AtlasSnackbar } from '@/components/ui'
import type { AtlasSnackbarSeverity } from '@/components/ui'
import { logger } from '@/utils/logger'
import { onMounted, ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from '@/composables/useI18n'
import VersionsTable from './VersionsTable.vue'
import VersionCommentDialog from './VersionCommentDialog.vue'
import { useVersions } from '@/composables/useVersions'
import { copyVersion as copyCohortVersion } from '@/services/cohort-definition-versions.service'
import { copyVersion as copyConceptSetVersion } from '@/services/concept-set-versions.service'
import { copyPathwayVersion } from '@/services/pathway-versions.service'
import { copyIncidenceRateVersion } from '@/services/incidence-rate-versions.service'
import { ASSET_ROUTE_SEGMENT, ASSET_DETAIL_ROUTE_SEGMENT } from './types'
import type { VersionsConfig, VersionsTableItem, Version } from './types'

// Props
const props = defineProps<{
  config: VersionsConfig
}>()

// Composables
const router = useRouter()
const { t, tv } = useI18n()
const versionManager = useVersions(props.config)

// Local state
const commentDialogOpen = ref(false)
const selectedVersion = ref<Version | null>(null)
const saveConfirmDialogOpen = ref(false)
const savingPreview = ref(false)
const snackbar = reactive({
  show: false,
  message: '',
  severity: 'success' as AtlasSnackbarSeverity,
})

// Copy dispatch keyed explicitly by assetType (a chained ternary's
// fall-through else is exactly what let an IR copy silently PUT to
// /conceptset/{irId}/... - see versions-navigation.spec.ts) so a future
// fifth asset type is a type error here instead of a silent mis-dispatch.
const COPY_VERSION_API: Record<
  VersionsConfig['assetType'],
  (assetId: number, versionNumber: number) => Promise<{ id?: number | string }>
> = {
  cohortdefinition: copyCohortVersion,
  conceptset: copyConceptSetVersion,
  'pathway-analysis': copyPathwayVersion,
  ir: copyIncidenceRateVersion,
}
const copyVersionAPI = COPY_VERSION_API[props.config.assetType]

// Load versions on mount
onMounted(async () => {
  await versionManager.loadVersions()
})

// Event handlers

/**
 * Handle preview action - navigate to version route
 * User Story 2 implementation (T035)
 */
function handlePreview(versionNumber: number): void {
  // Check for unsaved changes (T044)
  if (props.config.isDirty.value) {
    const confirmed = confirm(tv('common.unsavedWarning'))
    if (!confirmed) return
  }

  // Navigate to version preview route (T036)
  router.push({
    path: `/${ASSET_ROUTE_SEGMENT[props.config.assetType]}/${props.config.assetId}/version/${versionNumber}`,
  })
}

/**
 * Handle edit comment action
 * User Story 3 implementation (T049)
 */
function handleEditComment(item: VersionsTableItem): void {
  selectedVersion.value = item as Version
  commentDialogOpen.value = true
}

/**
 * Handle comment saved
 * T050: Optimistic update implementation
 */
async function handleCommentSaved(updatedVersion: Version): Promise<void> {
  // Optimistically update the version in the list
  const versionIndex = versionManager.versions.value.findIndex(
    v => v.version === updatedVersion.version
  )

  if (versionIndex !== -1) {
    const existing = versionManager.versions.value[versionIndex]
    if (existing) {
      existing.comment = updatedVersion.comment ?? null
    }
  }

  // Show success message
  showSnackbar(tv('versions.commentSaved'), 'success')
}

/**
 * Handle copy version action
 * User Story 4 implementation (T057, T059)
 */
async function handleCopy(versionNumber: number): Promise<void> {
  // Check for unsaved changes (T060)
  if (props.config.isDirty.value) {
    const confirmed = confirm(tv('common.unsavedWarning'))
    if (!confirmed) return
  }

  try {
    // Call copy API (T057)
    const newAsset = await copyVersionAPI(props.config.assetId, versionNumber)

    // Show success message
    showSnackbar(tv('versions.copySuccess'), 'success')

    // T059: Clear preview state before navigation
    if (props.config.previewVersion.value) {
      // We're copying while in preview mode - clear the preview state
      if (props.config.clearPreview) {
        props.config.clearPreview()
      } else {
        // Fallback to direct mutation if callback not provided
        // eslint-disable-next-line vue/no-mutating-props
        props.config.previewVersion.value = null
      }
    }

    // Navigate to new asset (T058). Concept sets have no id-addressable
    // route to navigate to (see ASSET_DETAIL_ROUTE_SEGMENT) - the copy
    // still succeeded (toast above), so just skip the navigation for it
    // rather than pushing a dead link.
    // The copy responses are loosely validated (z.any() for cohorts), so id
    // can be absent - skip navigation rather than pushing '/.../undefined'.
    const detailSegment = ASSET_DETAIL_ROUTE_SEGMENT[props.config.assetType]
    if (detailSegment && newAsset.id != null) {
      setTimeout(() => {
        router.push({
          path: `/${detailSegment}/${newAsset.id}`,
        })
      }, 1000)
    }
  } catch (error) {
    logger.error('VersionsTabContent', 'Failed to copy version', error)
    showSnackbar(tv('versions.copyError'), 'error')
  }
}

/**
 * Handle save preview as current
 * User Story 5 implementation (T065-T067)
 */
async function handleSavePreviewAsCurrent(): Promise<void> {
  saveConfirmDialogOpen.value = false
  savingPreview.value = true

  try {
    // T065: Call the appropriate store action to save preview as current
    // This will save the current preview state as the new current version
    const success = await savePreviewAsCurrent()

    if (success) {
      // The store's own savePreviewAsCurrent already nulls previewVersion on
      // success, which flows back here through the shared ref.
      showSnackbar(tv('versions.saveSuccess'), 'success')

      // T067: Refresh the versions list to show the new version
      await versionManager.loadVersions()
    } else {
      showSnackbar(tv('versions.saveError'), 'error')
    }
  } catch (error) {
    logger.error('VersionsTabContent', 'Failed to save preview as current', error)
    showSnackbar(tv('versions.saveError'), 'error')
  } finally {
    savingPreview.value = false
  }
}

/**
 * Get the appropriate save function based on asset type
 */
async function savePreviewAsCurrent(): Promise<boolean> {
  // Use dynamic imports to avoid circular dependencies
  if (props.config.assetType === 'cohortdefinition') {
    const { useCohortStore } = await import('@/stores/cohort')
    const cohortStore = useCohortStore()
    return cohortStore.savePreviewAsCurrent()
  } else if (props.config.assetType === 'pathway-analysis') {
    const { usePathwayStore } = await import('@/stores/pathway')
    return usePathwayStore().savePreviewAsCurrent()
  } else if (props.config.assetType === 'ir') {
    const { useIncidenceRateStore } = await import('@/stores/incidence-rate')
    return useIncidenceRateStore().savePreviewAsCurrent()
  } else {
    const { useConceptSetsStore } = await import('@/stores/concept-sets')
    const conceptSetStore = useConceptSetsStore()
    return conceptSetStore.savePreviewAsCurrent()
  }
}

/**
 * Show snackbar message
 */
function showSnackbar(message: string, color: 'success' | 'error'): void {
  snackbar.message = message
  snackbar.severity = color === 'error' ? 'danger' : color
  snackbar.show = true
}
</script>

<style scoped>
.versions-tab-content {
  padding: 1rem;
}
</style>
